<script setup lang="ts">
import LoyaltyHubCashbackCard from './CashbackCard.vue';
import LoyaltyHubRouletteVipCard from './RouletteVipCard.vue';
import LoyaltyHubLockedCard from './LockedCard.vue';
import type { LoyaltyHubCard } from '~/types/loyalty-hub';
import {
  isLoyaltyHubCashbackCard,
  loyaltyHubCardArt,
  loyaltyHubCardLockedArt,
} from '@/utils/loyalty-hub';
import { vipTierStyle } from '@/utils/vip-tiers';

// Cards de cima entram `eager` (são o que aparece na primeira tela do painel);
// o resto é `lazy` e só é buscado quando a lista rola até ele. Com a caixa da
// arte já reservada (ver `aspect-ratio` no estilo), adiar não mexe no layout.
const EAGER_CARDS = 2;

const props = defineProps<{
  card: LoyaltyHubCard;
  // Posição (0-based) no DOM, que é a ordem do MOBILE (`priority`). Serve só pra
  // decidir eager/lazy — no desktop o `order` do CSS reordena a mesma lista, e
  // imagem lazy que já está na viewport carrega na hora, então o split só adia
  // de fato o que está abaixo da dobra nos dois layouts.
  index?: number;
  // Posição (0-based) no grid do DESKTOP, que tem ordem própria no Figma — o
  // DOM segue a do mobile e aqui só o `order` do CSS reordena. Define também o
  // span: os 3 últimos formam a linha de 3 colunas.
  desktopIndex?: number;
  total?: number;
}>();

const emit = defineEmits<{ (e: 'select', card: LoyaltyHubCard): void }>();

// `enabled: false` no contrato = card desativado: segue na posição da `priority`
// dele, mas troca a arte ativa por um card COMPOSTO de bloqueio (LockedCard) e
// não é clicável. Ausente = ativo.
const isLocked = computed(() => props.card.enabled === false);

const lockedLabel = computed(() => props.card.lockedLabel || 'Em breve');

// Arte cinza da variante bloqueada — vem do contrato (`lockedImage`), não por
// convenção de caminho: card sem ela não pede um arquivo que não existe, só
// renderiza o bloqueado sem arte.
const lockedArt = computed(() => loyaltyHubCardLockedArt(props.card));

// Tratamento por tier de VIP: o card marcado com `vipTiers` no contrato troca a
// arte estática por um card COMPOSTO (RouletteVipCard) quando o jogador tem nível
// com arte — `order` 3 → gradiente verde + badge V3 + "Roleta Diária VIP 3".
// Jogador comum e os níveis ainda sem arte (o SIGNATURE, `order` 7) caem no card
// de imagem de sempre.
// `vipTier` chega `null` no primeiro frame e só resolve quando a resposta de
// /players/me aterrissa — o card padrão é sempre o que aparece primeiro.
const { vipTier } = useVipStatus();

// Vale nos dois breakpoints: o RouletteVipCard tem o desenho mobile (374 × 88) e
// o desktop (551,5 × 164) no mesmo componente, trocados por media query — não
// precisa de gate de largura aqui.
const vipStyle = computed(() =>
  props.card.vipTiers === true ? vipTierStyle(vipTier.value) : undefined,
);

// Cashback também é COMPOSTO em DOM (gradiente/texto/CTA em CSS, só o baú é
// imagem) — ver CashbackCard.vue. Diferente do card de VIP, não tem gate nenhum:
// é o desenho do card, não uma variação por estado do usuário.
const isCashback = computed(() => isLoyaltyHubCashbackCard(props.card));

// Cards informativos ('none'), bloqueados ('coming_soon') e desativados pelo
// contrato não são clicáveis.
const isInteractive = computed(
  () =>
    !isLocked.value &&
    props.card.action !== 'coming_soon' &&
    props.card.action !== 'none',
);

// Card inteiro = 1 imagem exportada do Figma (gradiente + arte + texto + CTA,
// com o bleed embutido como transparência no topo). Mobile e desktop têm artes
// diferentes (layout distinto); o <picture> troca por breakpoint.
const bgUrl = computed(() => loyaltyHubCardArt(props.card));

const desktopUrl = computed(() => loyaltyHubCardArt(props.card, true));

const isEager = computed(() => (props.index ?? 0) < EAGER_CARDS);

// Fade-in da arte: sem isso ela aparece de estalo dentro da caixa reservada.
const imgEl = ref<HTMLImageElement | null>(null);
const isLoaded = ref(false);

// Arte já em cache pode chegar `complete` antes do listener existir — aí o
// evento `load` nunca dispara e o card ficaria preso em `opacity: 0`.
onMounted(() => {
  if (imgEl.value?.complete) isLoaded.value = true;
});

// Últimos 3 cards DA ORDEM DESKTOP = linha de 3 colunas (span menor).
const isLast3 = computed(
  () =>
    props.desktopIndex !== undefined &&
    props.total !== undefined &&
    props.desktopIndex >= props.total - 3,
);

// Texto está embutido na imagem — repassa pro alt pra manter acessibilidade.
// (Nos cards compostos — VIP, cashback e bloqueado — isto não se aplica: lá o
// texto é DOM de verdade.)
const alt = computed(() =>
  [props.card.title, props.card.subtitle].filter(Boolean).join('. '),
);

function onSelect() {
  if (!isInteractive.value) return;
  emit('select', props.card);
}
</script>

<template>
  <!-- Card BLOQUEADO: também é composto, e vem ANTES do de VIP — bloqueado é
       bloqueado mesmo num card que tem versão por tier. -->
  <LoyaltyHubLockedCard
    v-if="isLocked"
    :art="lockedArt"
    :class="[`lh-card--${card.layout}`, { 'lh-card--last3': isLast3 }]"
    :label="lockedLabel"
    :layout="card.layout"
    :style="{ '--lh-desktop-order': desktopIndex }"
    :title="card.title"
  />
  <!-- Card composto por tier de VIP: substitui a arte estática inteira, então
       entra no lugar do <button> e recebe as mesmas classes de span do grid (o
       scoped CSS daqui alcança a raiz do filho). -->
  <LoyaltyHubRouletteVipCard
    v-else-if="vipStyle"
    :class="[`lh-card--${card.layout}`, { 'lh-card--last3': isLast3 }]"
    :cta="card.cta"
    :style="{ '--lh-desktop-order': desktopIndex }"
    :subtitle="card.subtitle"
    :tier="vipTier as number"
    :title="card.title"
    @select="onSelect"
  />

  <!-- Card de cashback composto em DOM: entra no lugar do <button> pelo mesmo
       esquema do card de VIP (as classes de span do grid vêm daqui). -->
  <LoyaltyHubCashbackCard
    v-else-if="isCashback"
    :class="[`lh-card--${card.layout}`, { 'lh-card--last3': isLast3 }]"
    :cta="card.cta"
    :style="{ '--lh-desktop-order': desktopIndex }"
    :subtitle="card.subtitle"
    :title="card.title"
    @select="onSelect"
  />

  <button
    v-else
    class="lh-card"
    :class="[
      `lh-card--${card.layout}`,
      {
        'lh-card--last3': isLast3,
        'lh-card--inert': !isInteractive,
      },
    ]"
    :disabled="!isInteractive"
    :style="{ '--lh-desktop-order': desktopIndex }"
    type="button"
    @click="onSelect"
    @dragstart.prevent
  >
    <picture>
      <source
        media="(min-width: 1024px)"
        :srcset="desktopUrl"
      />
      <img
        ref="imgEl"
        :alt="alt"
        class="lh-card__img"
        :class="{ 'is-loaded': isLoaded }"
        decoding="async"
        :loading="isEager ? 'eager' : 'lazy'"
        :src="bgUrl"
        @error="isLoaded = true"
        @load="isLoaded = true"
      />
    </picture>
  </button>
</template>

<style scoped lang="scss">
// O card É a imagem (tamanho/cantos/bleed já vêm do Figma).
.lh-card {
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  // Bleed da arte transborda pra cima sem ser cortado.
  overflow: visible;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  // <picture> transparente ao layout: a <img> se comporta como filha do card.
  picture {
    display: contents;
  }

  // Card sem ação (desativado, 'coming_soon' ou 'none') não intercepta ponteiro:
  // sem cursor de mão e sem clique. O `:disabled` já suprimiria o evento, mas
  // suprimir não é o mesmo que repassar — sem isto a caixa dele continuaria
  // engolindo o toque em vez de deixar passar pro que houver atrás.
  // (O bleed sobreposto ao card anterior é problema de todo card, ativo ou não,
  // e quem resolve é o `pointer-events: none` da .lh-card__img mais abaixo.)
  &.lh-card--inert {
    pointer-events: none;
  }

  &:not(:disabled):active {
    filter: brightness(0.94);
  }
}

.lh-card__img {
  display: block;
  width: 100%;
  height: auto;
  // A arte NUNCA é alvo de clique — quem recebe é a caixa do <button>, que é
  // exatamente o corpo do card. Sem isso, a faixa de bleed (que sobe por
  // margin-top negativo e é maior que o gap: 32px contra 16px no mobile, 48
  // contra 16 no desktop) fica pairando sobre os últimos pixels do card ANTERIOR
  // e rouba o clique dele — era o bug de "clicar na base de um card abre o card
  // seguinte". Como a <img> sai do hit-test, o ponteiro atravessa o bleed e acha
  // a caixa de quem está embaixo: o card certo.
  pointer-events: none;
  -webkit-user-drag: none;
  user-select: none;
  // Escondida até carregar; o fade entra pelo `.is-loaded` (via `animation`, e
  // não `transition`, porque o reset global do main.scss zera transition no app
  // inteiro). O `@error` também liga a classe, pra arte quebrada não sumir.
  opacity: 0;
}

.lh-card__img.is-loaded {
  animation: lh-card-img-in 0.18s ease forwards;
}

@keyframes lh-card-img-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* ---------- span no grid de 6 colunas ---------- */
// Mobile: full ocupa a linha toda; half = metade (2 por linha).
.lh-card--full {
  grid-column: span 6;
}

.lh-card--half {
  grid-column: span 3;
}

// RESERVA DA CAIXA DA ARTE (`aspect-ratio`): sem isso a <img> mede ZERO de
// altura até o byte chegar — e como o card É a imagem (nenhum texto, nenhuma
// altura mínima no DOM), o painel abria colapsado e ia se esticando conforme
// cada webp caía, empurrando os de baixo. Era o tilt da abertura sem cache.
// `auto <razão>` e não `<razão>` seco: a razão declarada só vale ENQUANTO não
// existe a intrínseca; depois de carregar quem manda é a imagem. Assim um asset
// de proporção diferente vindo do contrato (`background`) não estica.
// Os números são as dimensões reais das artes exportadas do Figma, que são
// uniformes por classe: mobile full 1122x360 e half 537x492.
// Mobile full: imagem 374x120 = corpo (88) + bleed (32) no topo. margin-top
// -32/374 = -8.556% puxa o bleed pra cima sem inflar o gap.
.lh-card--full .lh-card__img {
  aspect-ratio: auto 1122 / 360;
  margin-top: -8.556%;
}

.lh-card--half .lh-card__img {
  aspect-ratio: auto 537 / 492;
}

/* ---------- desktop (2-col + última linha de 3) ---------- */
@media (min-width: 1024px) {
  // O desktop tem ordem PRÓPRIA no Figma (check-in abre a lista, loja desce pra
  // linha de 3) e o DOM segue a do mobile — então quem reordena aqui é o
  // `order`, que o auto-placement do grid respeita. Sem valor (contrato antigo,
  // sem `desktopPriority`), todos ficam em 0 e vale a ordem do DOM.
  // Vai nas classes de LAYOUT e não em `.lh-card` porque o card composto de VIP
  // (RouletteVipCard) é raiz própria e não carrega `.lh-card` — ela força
  // `display: block` e quebraria o flex dele. As de layout ele recebe.
  .lh-card--full,
  .lh-card--half {
    order: var(--lh-desktop-order, 0);
    // Todos viram 2-col (span 3); os 3 últimos, 3-col (span 2), mais abaixo.
    grid-column: span 3;
  }

  .lh-card--last3 {
    grid-column: span 2;
  }

  // Desktop: imagem 552x212 (2-col) ou 362x212 (3-col), corpo 164 + bleed 48.
  // margin-top = -bleed/largura pro overlap uniforme (escala responsivo).
  // `aspect-ratio`: dimensões reais das artes desktop — 1655x636 nas de 2-col e
  // 1098x636 nas 3-col (mesmo motivo do mobile, ver acima). A ÚLTIMA célula da
  // linha de 3 sai do Figma 11px mais estreita (1087) — hoje é a do
  // `descontos-loja`, que é quem tem `desktopPriority` 70. Dá ~2px de diferença
  // na tela, que o `auto` acerta no load.
  .lh-card--full .lh-card__img,
  .lh-card--half .lh-card__img {
    aspect-ratio: auto 1655 / 636;
    margin-top: -8.696%; // 48 / 552
  }

  .lh-card--last3 .lh-card__img {
    aspect-ratio: auto 1098 / 636;
    margin-top: -13.26%; // 48 / 362
  }
}
</style>
